import * as THREE from 'three';
import { Entity } from '../../entities';
import { createMatteMaterial, createGlowMaterial } from '../../visual';

/**
 * Recovery tug — bigger, slower, industrial-looking ship that ferries
 * stranded pilots back to the station. The model is built standalone (no
 * faction skin) so the silhouette is immediately distinct from the
 * player or the NPC interceptors:
 *
 *   - chunky orange utility hull with grey trim
 *   - flat tow-arm / clamp-fork extending from the front
 *   - amber strobes pulsing on top
 *   - oversized rear engine bell with a steady plasma glow
 *
 * Forward axis is local -Z so heading-vector math (`atan2`) matches the
 * convention the rest of the ship code uses. The entity exposes
 * `velocity` so the rescue system can drive it like any other
 * cruise-speed vehicle.
 */
export class TugShip extends Entity {
  readonly velocity = new THREE.Vector3();
  readonly collisionRadius: number;

  private readonly engineGlow: THREE.Mesh;
  private readonly engineCore: THREE.Mesh;
  private readonly strobes: THREE.Mesh[] = [];
  private time = 0;
  private thrust = 0;

  constructor() {
    super();
    this.object3D.name = 'TugShip';

    const hullMat = createMatteMaterial({ color: 0xc16b1f, roughness: 0.65 });
    const darkMat = createMatteMaterial({ color: 0x3a2c1a, roughness: 0.85 });
    const trimMat = createMatteMaterial({ color: 0x88673c, roughness: 0.55 });
    const accentMat = createMatteMaterial({
      color: 0xffd14a,
      roughness: 0.45,
      emissive: 0xffa84a,
      emissiveIntensity: 0.18
    });

    // Scale factor — the tug is intentionally chunky so it reads as
    // "industrial recovery vessel" against the player's interceptor at
    // a glance. Sized roughly 3x the player's hull dimensions.
    const S = 1.75;

    /* ─── Main hull — wide stubby box with bevel suggestions ─── */
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(3.6 * S, 1.55 * S, 4.4 * S),
      hullMat
    );
    body.position.set(0, 0.2 * S, 0);
    this.object3D.add(body);

    const belly = new THREE.Mesh(
      new THREE.BoxGeometry(3.3 * S, 0.7 * S, 4.1 * S),
      darkMat
    );
    belly.position.set(0, -0.6 * S, 0);
    this.object3D.add(belly);

    // Top ridge — running across the spine for silhouette break-up.
    const ridge = new THREE.Mesh(
      new THREE.BoxGeometry(2.2 * S, 0.35 * S, 3.6 * S),
      trimMat
    );
    ridge.position.set(0, 1.05 * S, 0);
    this.object3D.add(ridge);

    // Hazard stripe along the side.
    const stripeGeo = new THREE.BoxGeometry(0.08 * S, 0.28 * S, 4.0 * S);
    const stripeMat = createMatteMaterial({
      color: 0xfff0a0,
      roughness: 0.4,
      emissive: 0xffd14a,
      emissiveIntensity: 0.4
    });
    const stripeL = new THREE.Mesh(stripeGeo, stripeMat);
    stripeL.position.set(-1.85 * S, 0.18 * S, 0);
    this.object3D.add(stripeL);
    const stripeR = stripeL.clone();
    stripeR.position.x = 1.85 * S;
    this.object3D.add(stripeR);

    // Cockpit — small forward bubble.
    const cockpitMat = createMatteMaterial({
      color: 0x132030,
      roughness: 0.2,
      emissive: 0x6cb1ff,
      emissiveIntensity: 0.32
    });
    const cockpit = new THREE.Mesh(
      new THREE.BoxGeometry(1.4 * S, 0.7 * S, 1.0 * S),
      cockpitMat
    );
    cockpit.position.set(0, 0.85 * S, -1.2 * S);
    this.object3D.add(cockpit);

    /* ─── Tow arm — flat fork extending from the front ─── */
    const armMat = createMatteMaterial({ color: 0x8a6324, roughness: 0.7 });
    const armBase = new THREE.Mesh(
      new THREE.BoxGeometry(2.4 * S, 0.35 * S, 0.9 * S),
      armMat
    );
    armBase.position.set(0, -0.3 * S, -2.7 * S);
    this.object3D.add(armBase);

    const forkLeft = new THREE.Mesh(
      new THREE.BoxGeometry(0.32 * S, 0.32 * S, 1.6 * S),
      armMat
    );
    forkLeft.position.set(-1.0 * S, -0.3 * S, -3.7 * S);
    this.object3D.add(forkLeft);
    const forkRight = forkLeft.clone();
    forkRight.position.x = 1.0 * S;
    this.object3D.add(forkRight);

    // Tip clamps — small accent boxes at fork ends.
    const clampL = new THREE.Mesh(
      new THREE.BoxGeometry(0.5 * S, 0.5 * S, 0.45 * S),
      accentMat
    );
    clampL.position.set(-1.0 * S, -0.3 * S, -4.55 * S);
    this.object3D.add(clampL);
    const clampR = clampL.clone();
    clampR.position.x = 1.0 * S;
    this.object3D.add(clampR);

    /* ─── Side cargo pods ─── */
    const podGeo = new THREE.BoxGeometry(0.7 * S, 0.85 * S, 1.8 * S);
    const podMat = createMatteMaterial({ color: 0x6e4a26, roughness: 0.8 });
    const podL = new THREE.Mesh(podGeo, podMat);
    podL.position.set(-2.0 * S, 0.0, 0.5 * S);
    this.object3D.add(podL);
    const podR = podL.clone();
    podR.position.x = 2.0 * S;
    this.object3D.add(podR);

    /* ─── Rear engine bell + glow ─── */
    const bell = new THREE.Mesh(
      new THREE.CylinderGeometry(0.7 * S, 0.95 * S, 0.85 * S, 14, 1, true),
      darkMat
    );
    bell.rotation.x = Math.PI / 2;
    bell.position.set(0, 0.05 * S, 2.6 * S);
    this.object3D.add(bell);

    this.engineCore = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55 * S, 0.55 * S, 0.4 * S, 14),
      createGlowMaterial(0x6cb1ff, 0.92, false)
    );
    this.engineCore.rotation.x = Math.PI / 2;
    this.engineCore.position.set(0, 0.05 * S, 2.95 * S);
    this.object3D.add(this.engineCore);

    this.engineGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.95 * S, 12, 10),
      createGlowMaterial(0x6cb1ff, 0.55, false)
    );
    this.engineGlow.position.set(0, 0.05 * S, 3.2 * S);
    this.engineGlow.scale.set(1.4, 1.4, 2.2);
    this.object3D.add(this.engineGlow);

    /* ─── Top strobes (amber utility lights) ─── */
    const strobeMat = createGlowMaterial(0xffd14a, 1.0, false);
    for (const x of [-1.2 * S, 1.2 * S]) {
      const strobe = new THREE.Mesh(
        new THREE.SphereGeometry(0.18 * S, 8, 6),
        strobeMat
      );
      strobe.position.set(x, 1.32 * S, 0.5 * S);
      this.object3D.add(strobe);
      this.strobes.push(strobe);
    }

    this.collisionRadius = 4.2 * S;
  }

  setThrust(level: number): void {
    this.thrust = THREE.MathUtils.clamp(level, 0, 1);
  }

  /** Aim the tug along its current velocity vector (forward = local -Z). */
  faceVelocity(dt: number): void {
    const v = this.velocity;
    if (v.lengthSq() < 0.001) return;
    const targetYaw = Math.atan2(v.x, v.z) + Math.PI;
    const cur = this.object3D.rotation.y;
    let diff = targetYaw - cur;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    this.object3D.rotation.y = cur + diff * Math.min(1, dt * 3);
  }

  override update(dt: number): void {
    this.time += dt;

    // Animated engine — pulse opacity with thrust.
    const flame = 0.55 + Math.sin(this.time * 18) * 0.08;
    const coreMat = this.engineCore.material as THREE.MeshBasicMaterial;
    const glowMat = this.engineGlow.material as THREE.MeshBasicMaterial;
    coreMat.opacity = 0.6 + this.thrust * 0.4;
    glowMat.opacity = 0.25 + this.thrust * flame;
    this.engineGlow.scale.setZ(1.6 + this.thrust * 1.4);

    // Strobes — slow blink.
    const blink = (Math.sin(this.time * 4) + 1) * 0.5;
    const strobeMat = this.strobes[0]?.material as
      | THREE.MeshBasicMaterial
      | undefined;
    if (strobeMat) strobeMat.opacity = 0.3 + blink * 0.7;
  }
}
